import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IPartCategorySubPartLinksResponse,
  IPartCategorySubPartsResponse,
} from "../models/PartCategorySubPart";

export default function usePartCategorySubParts(categoryId?: string | undefined) {
  const { get, post, put } = useApiHelper();
  const queryClient = useQueryClient();

  // Deliberately a TOP-LEVEL key rather than ["categories", "subPartLinks"].
  // invalidateQueries(["categories"]) in useCategoriesMutations / useCategorySingle
  // prefix-matches everything nested under it, which would refetch this on every
  // category edit. CreateJobs depends on this query, so it stays out of that blast radius.
  const linksQueryKey = ["categorySubPartLinks"];

  const subPartLinks = useQuery({
    queryKey: linksQueryKey,
    queryFn: () => get<IPartCategorySubPartLinksResponse>("/part/categories/subpart-links"),
    staleTime: 1000 * 60 * 60, // 60 minutes - admin config, changes rarely
  });

  const subParts = useQuery({
    queryKey: ["categories", categoryId, "subParts"],
    queryFn: () => get<IPartCategorySubPartsResponse>(`/part/categories/${categoryId}/subparts`),
    enabled: !!categoryId,
  });

  const invalidateQueries = () => {
    queryClient.invalidateQueries(linksQueryKey);
    queryClient.invalidateQueries(["categories", categoryId, "subParts"]);
    // hasSubParts / isSubPartOfMain live on the partial projection
    queryClient.invalidateQueries(["categories", "partial"]);
  };

  const toggleSubPart = useMutation(
    ({ mainCategoryId, subPartCategoryId }: { mainCategoryId: string; subPartCategoryId: string }) =>
      post(`/part/categories/${mainCategoryId}/subparts/toggle/${subPartCategoryId}`, {}),
    {
      onSuccess: () => {
        invalidateQueries();
      },
    }
  );

  const setSubParts = useMutation(
    ({ mainCategoryId, subPartCategoryIds }: { mainCategoryId: string; subPartCategoryIds: string[] }) =>
      put(`/part/categories/${mainCategoryId}/subparts`, { subPartCategoryIds }),
    {
      onSuccess: () => {
        invalidateQueries();
      },
    }
  );

  return {
    subPartLinks,
    subParts,
    toggleSubPart,
    setSubParts,
  };
}
